import { createFileRoute, useParams } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Button,
  Card,
  Drawer,
  Group,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconArchive,
  IconDownload,
  IconFileSpreadsheet,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlayerStop,
  IconRefresh,
  IconRotate,
} from '@tabler/icons-react';
import { Menu } from '@mantine/core';
import { motion } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { useEffect, useMemo, useState } from 'react';
import { trpc } from '../../lib/trpc';
import { getSocket } from '../../lib/socket';

const TALLY_COLOR: Record<string, string> = {
  InQueue: 'blue',
  Failed: 'red',
  Processing: 'yellow',
  Processed: 'green',
  Expired: 'orange',
  Paused: 'gray',
  Cancelled: 'gray',
};

// Queue.status integer → human label (matches dispatcher overlay map).
const QUEUE_STATUS_LABEL: Record<number, string> = {
  1: 'InQueue',
  2: 'Failed',
  3: 'Processing',
  4: 'Sent',
  5: 'Expired',
  6: 'Paused',
  7: 'Cancelled',
  50: 'Sent',
  51: 'Delivered',
  52: 'Read',
};
const QUEUE_STATUS_COLOR: Record<number, string> = {
  1: 'blue',
  2: 'red',
  3: 'yellow',
  4: 'green',
  5: 'orange',
  6: 'gray',
  7: 'gray',
  50: 'green',
  51: 'green',
  52: 'cyan',
};

interface QueueRow {
  id: number;
  phoneWithCountryCode: string | null;
  status: number;
  retries: number;
  scheduledAt: Date | string | null;
  lastError?: string | null;
  variantKey?: string | null;
  deliveryStatus?: string | null;
}

function CampaignStatus() {
  const { uid } = useParams({ from: '/vendor/campaigns/$uid/status' });
  const status = trpc.campaigns.getStatusByUid.useQuery({ uid });
  const utils = trpc.useUtils();
  const campaignId = status.data?.id ?? null;
  const aggregate = trpc.campaigns.reportAggregate.useQuery(
    { campaignId: campaignId! },
    { enabled: !!campaignId },
  );
  const cohorts = trpc.campaigns.clickCohorts.useQuery(
    { campaignId: campaignId! },
    { enabled: !!campaignId },
  );
  const queueItems = trpc.campaigns.getQueueItems.useQuery(
    { campaignId: campaignId!, limit: 500 },
    { enabled: !!campaignId },
  );

  const refresh = () => {
    void utils.campaigns.getStatusByUid.invalidate({ uid });
    if (campaignId) {
      void utils.campaigns.reportAggregate.invalidate({ campaignId });
      void utils.campaigns.getQueueItems.invalidate({ campaignId });
    }
  };

  // Phase 3 — live updates via Socket.IO. The webhook handler publishes
  // 'StatusUpdate' with campaignId so we filter and only re-fetch when
  // the event affects this campaign. Debounced via the trailing edge of
  // a 400ms timer so a 100-row burst causes one refetch, not 100.
  useEffect(() => {
    if (!campaignId) return;
    const socket = getSocket();
    let timer: ReturnType<typeof setTimeout> | null = null;
    const trigger = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void utils.campaigns.reportAggregate.invalidate({ campaignId });
        void utils.campaigns.getQueueItems.invalidate({ campaignId });
        void utils.campaigns.getStatusByUid.invalidate({ uid });
      }, 400);
    };
    const onEvent = (e: { eventType?: string; campaignId?: number | null }) => {
      if (e.eventType !== 'StatusUpdate') return;
      if (e.campaignId !== campaignId) return;
      trigger();
    };
    socket.on('VendorChannelBroadcast', onEvent);
    return () => {
      socket.off('VendorChannelBroadcast', onEvent);
      if (timer) clearTimeout(timer);
    };
  }, [campaignId, uid, utils]);

  const pause = trpc.campaigns.pause.useMutation({
    onSuccess: (r) => {
      notifications.show({ message: `Paused ${r.updated}`, color: 'green' });
      refresh();
    },
  });
  const resume = trpc.campaigns.resume.useMutation({
    onSuccess: (r) => {
      notifications.show({ message: `Resumed ${r.updated}`, color: 'green' });
      refresh();
    },
  });
  const cancel = trpc.campaigns.cancel.useMutation({
    onSuccess: (r) => {
      notifications.show({ message: `Cancelled ${r.updated}`, color: 'orange' });
      refresh();
    },
  });
  const archive = trpc.campaigns.archive.useMutation({ onSuccess: refresh });
  const requeueRow = trpc.campaigns.requeueRow.useMutation({
    onSuccess: (r) => {
      notifications.show({ message: `Requeued ${r.updated}`, color: 'green' });
      refresh();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const [drillRow, setDrillRow] = useState<QueueRow | null>(null);

  const variantTallies = useMemo(() => {
    return aggregate.data?.variants ?? {};
  }, [aggregate.data]);

  if (status.isPending || !status.data) return <Skeleton height={500} />;
  const c = status.data;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group justify="space-between">
          <div>
            <Title order={2}>{c.title}</Title>
            <Text c="dimmed" size="sm">
              {c.channel === 1 ? 'WhatsApp' : 'Instagram'} ·{' '}
              {c.scheduledAt ? new Date(c.scheduledAt).toLocaleString() : 'No schedule'}
            </Text>
          </div>
          <Group>
            <Button
              variant="default"
              leftSection={<IconRefresh size={14} />}
              loading={status.isFetching && status.isFetched}
              onClick={refresh}
            >
              Refresh
            </Button>
            <Menu shadow="md">
              <Menu.Target>
                <Button variant="default" leftSection={<IconDownload size={14} />}>
                  Export
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>CSV</Menu.Label>
                <Menu.Item
                  leftSection={<IconDownload size={14} />}
                  component="a"
                  href={`/api/campaigns/${c.id}/export.csv`}
                >
                  All recipients (CSV)
                </Menu.Item>
                <Menu.Divider />
                <Menu.Label>XLSX</Menu.Label>
                <Menu.Item
                  leftSection={<IconFileSpreadsheet size={14} />}
                  component="a"
                  href={`/api/campaigns/${c.id}/export.xlsx?kind=executed`}
                >
                  Executed
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconFileSpreadsheet size={14} />}
                  component="a"
                  href={`/api/campaigns/${c.id}/export.xlsx?kind=queue`}
                >
                  Queue log
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconFileSpreadsheet size={14} />}
                  component="a"
                  href={`/api/campaigns/${c.id}/export.xlsx?kind=expired`}
                >
                  Expired / failed
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
            <Button
              variant="light"
              color="orange"
              leftSection={<IconPlayerPause size={14} />}
              onClick={() => pause.mutate({ campaignId: c.id })}
            >
              Pause
            </Button>
            <Button
              variant="light"
              leftSection={<IconPlayerPlay size={14} />}
              onClick={() => resume.mutate({ campaignId: c.id })}
            >
              Resume
            </Button>
            <Button
              variant="light"
              color="red"
              leftSection={<IconPlayerStop size={14} />}
              onClick={() => cancel.mutate({ campaignId: c.id })}
            >
              Cancel
            </Button>
            <Button
              variant="light"
              leftSection={<IconArchive size={14} />}
              onClick={() => archive.mutate({ campaignId: c.id })}
            >
              Archive
            </Button>
          </Group>
        </Group>

        {c.qualityHaltedAt && (
          <Alert color="red" icon={<IconAlertTriangle />} title="Auto-paused on quality drop">
            <Text size="sm" mb="xs">
              {c.qualityHaltReason ?? 'Phone quality dropped — campaign was paused automatically.'}
            </Text>
            <Text size="xs" c="dimmed">
              {new Date(c.qualityHaltedAt).toLocaleString()}. Quality probes refresh every 5 min;
              click Resume once your phone returns to GREEN to retry.
            </Text>
          </Alert>
        )}

        <Card>
          <Title order={5} mb="xs">
            Tally
          </Title>
          <Group wrap="wrap">
            {Object.entries(c.tally).map(([k, v]) => (
              <Badge key={k} color={TALLY_COLOR[k] ?? 'gray'} variant="light" size="lg">
                {k}: {v}
              </Badge>
            ))}
            <Badge color="grape" variant="light" size="lg">
              Replied: {c.replied}
            </Badge>
            <Badge color="cyan" variant="light" size="lg">
              Clicked: {c.clicked}
            </Badge>
            {(aggregate.data?.channels?.mm_lite ?? 0) > 0 && (
              <Badge color="teal" variant="light" size="lg">
                Sent via MM Lite: {aggregate.data!.channels.mm_lite}
              </Badge>
            )}
          </Group>
        </Card>

        {c.funnel && (
          <Card>
            <Title order={5} mb="xs">
              Delivery funnel
            </Title>
            <Group gap="xl" align="flex-end" wrap="wrap">
              <FunnelStep label="Sent" value={c.funnel.sent} />
              <FunnelStep
                label="Delivered"
                value={c.funnel.delivered}
                pct={pct(c.funnel.delivered, c.funnel.sent)}
              />
              <FunnelStep
                label="Read"
                value={c.funnel.read}
                pct={pct(c.funnel.read, c.funnel.delivered || c.funnel.sent)}
              />
              <FunnelStep
                label="Failed"
                value={c.funnel.failed}
                pct={pct(c.funnel.failed, c.funnel.sent + c.funnel.failed)}
                color="red"
              />
            </Group>
          </Card>
        )}

        {cohorts.data && cohorts.data.total > 0 && (
          <Card>
            <Title order={5} mb="xs">
              Click cohorts ({cohorts.data.humanCount} human / {cohorts.data.botCount} bot of{' '}
              {cohorts.data.total} total)
            </Title>
            <Group align="flex-start" wrap="wrap" gap="lg">
              <Stack gap={4}>
                <Text size="xs" fw={500} c="dimmed">
                  By device
                </Text>
                {Object.entries(cohorts.data.byDevice)
                  .filter(([, n]) => n > 0)
                  .map(([k, n]) => (
                    <Badge key={k} variant="light">
                      {k}: {n}
                    </Badge>
                  ))}
              </Stack>
              <Stack gap={4}>
                <Text size="xs" fw={500} c="dimmed">
                  By hour
                </Text>
                <Group gap={2}>
                  {Object.entries(cohorts.data.byHour)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([h, n]) => (
                      <Tooltip key={h} label={`${h}:00 — ${n} clicks`}>
                        <Badge size="xs" variant="light">
                          {h}h:{n}
                        </Badge>
                      </Tooltip>
                    ))}
                </Group>
              </Stack>
              {Object.keys(cohorts.data.byCountry).length > 0 && (
                <Stack gap={4}>
                  <Text size="xs" fw={500} c="dimmed">
                    By country
                  </Text>
                  <Group gap={2}>
                    {Object.entries(cohorts.data.byCountry).map(([c2, n]) => (
                      <Badge key={c2} variant="light">
                        {c2}: {n}
                      </Badge>
                    ))}
                  </Group>
                </Stack>
              )}
            </Group>
          </Card>
        )}

        {Object.keys(variantTallies).length > 0 && (
          <Card>
            <Title order={5} mb="xs">
              A/B variants
            </Title>
            <Table withRowBorders={false}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Variant</Table.Th>
                  <Table.Th>Total</Table.Th>
                  <Table.Th>Sent</Table.Th>
                  <Table.Th>Failed</Table.Th>
                  <Table.Th>Replied</Table.Th>
                  <Table.Th>Clicked</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {Object.entries(variantTallies).map(([key, t]) => (
                  <Table.Tr key={key}>
                    <Table.Td>
                      <Badge variant="light">{key}</Badge>
                    </Table.Td>
                    <Table.Td>{t.total}</Table.Td>
                    <Table.Td>{t.sent}</Table.Td>
                    <Table.Td>{t.failed}</Table.Td>
                    <Table.Td>{t.replied}</Table.Td>
                    <Table.Td>{t.clicked}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
        )}

        <Card p={0}>
          <Title order={5} m="md">
            Queue (last 500)
          </Title>
          {queueItems.isPending ? (
            <Skeleton height={300} m="md" />
          ) : (
            <Table striped withRowBorders={false} highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Phone</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Variant</Table.Th>
                  <Table.Th>Retries</Table.Th>
                  <Table.Th>Last error</Table.Th>
                  <Table.Th>Scheduled</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(queueItems.data ?? []).map((q) => {
                  const label = QUEUE_STATUS_LABEL[q.status] ?? `status_${q.status}`;
                  const colour = QUEUE_STATUS_COLOR[q.status] ?? 'gray';
                  return (
                    <Table.Tr
                      key={q.id}
                      style={{ cursor: q.lastError ? 'pointer' : 'default' }}
                      onClick={() => q.lastError && setDrillRow(q as QueueRow)}
                    >
                      <Table.Td>
                        <Text ff="monospace" size="sm">
                          {q.phoneWithCountryCode ?? '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={colour} variant="light" size="xs">
                          {label}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {q.variantKey ? (
                          <Badge variant="light" size="xs">
                            {q.variantKey}
                          </Badge>
                        ) : (
                          <Text size="xs" c="dimmed">
                            —
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>{q.retries}</Table.Td>
                      <Table.Td>
                        {q.lastError ? (
                          <Tooltip label={q.lastError} multiline w={300}>
                            <Text size="xs" c="red" lineClamp={1}>
                              {q.lastError}
                            </Text>
                          </Tooltip>
                        ) : (
                          <Text size="xs" c="dimmed">
                            —
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {q.scheduledAt ? new Date(q.scheduledAt).toLocaleString() : '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td align="right">
                        {(q.status === 2 || q.status === 5) && (
                          <Button
                            size="xs"
                            variant="light"
                            leftSection={<IconRotate size={12} />}
                            loading={requeueRow.isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              requeueRow.mutate({ rowId: q.id });
                            }}
                          >
                            Retry
                          </Button>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          )}
        </Card>
      </Stack>

      <Drawer
        opened={!!drillRow}
        onClose={() => setDrillRow(null)}
        title="Send failure detail"
        position="right"
        size="md"
      >
        {drillRow && (
          <Stack gap="xs">
            <Text size="sm">
              <b>Phone:</b> {drillRow.phoneWithCountryCode ?? '—'}
            </Text>
            <Text size="sm">
              <b>Retries:</b> {drillRow.retries}
            </Text>
            <Text size="sm">
              <b>Variant:</b> {drillRow.variantKey ?? '—'}
            </Text>
            <Text size="sm" fw={500} mt="md">
              Error
            </Text>
            <Card withBorder>
              <Text size="xs" ff="monospace" style={{ whiteSpace: 'pre-wrap' }}>
                {drillRow.lastError ?? '(no error captured)'}
              </Text>
            </Card>
            <Group mt="md">
              <Button
                leftSection={<IconRotate size={14} />}
                loading={requeueRow.isPending}
                onClick={() => {
                  requeueRow.mutate({ rowId: drillRow.id });
                  setDrillRow(null);
                }}
              >
                Retry this recipient
              </Button>
            </Group>
          </Stack>
        )}
      </Drawer>
    </motion.div>
  );
}

function FunnelStep({
  label,
  value,
  pct,
  color = 'blue',
}: {
  label: string;
  value: number;
  pct?: number | null;
  color?: string;
}) {
  return (
    <Stack gap={2} align="flex-start">
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="xl" fw={600} c={color}>
        {value.toLocaleString()}
      </Text>
      {pct !== undefined && pct !== null && (
        <Text size="xs" c="dimmed">
          {pct.toFixed(1)}%
        </Text>
      )}
    </Stack>
  );
}

function pct(numerator: number, denominator: number): number | null {
  if (!denominator) return null;
  return (numerator / denominator) * 100;
}

export const Route = createFileRoute('/vendor/campaigns/$uid/status')({
  component: CampaignStatus,
});
